import * as React from 'react';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

// @mui
import { Container, Tabs, Tab, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
//--------------------------------------------------------
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Assignment as AssignmentIcon,
  Badge as BadgeIcon,
  AccountTree as AccountTreeIcon,
  GppGood as GppGoodIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import CommentIcon from '@mui/icons-material/Comment';
//--------------------------------------------------------
import { MultipleTabPanel, a11yProps } from '../../../utils/TabStyles';
import NotesTab from './partials/notesTab';
import AuthorizationsTab from './partials/authorizationsTab';
import BadgesTab from './partials/badgesTab';
import PositionsTab from './partials/positionsTab';
import CardsTab from './partials/cardsTab';
import UserDataTab from './partials/userDataTab';
import { UserProvider } from './context/UserContext';
import EntitiesTab from './partials/entitiesTab';

// ----------------------------------------------------------------------

dayjs.locale('it');

export default function UserDetails() {
  // general
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const { state } = useLocation();
  const userID = state.userID;
  const [currentProfile, setCurrentProfile] = useState('');

  // user section tab
  const [value, setValue] = useState(0);
  const handleChangeUserTabs = (event, newValue) => {
    setValue(newValue);
  };

  /* ------------------------------ User details ------------------------------ */
  const [canDeleteCards, setCanDeleteCards] = useState(false);
  const [canManageUser, setCanManageUser] = useState(false);
  const [canManageUserRoles, setCanManageUserRoles] = useState(false);
  const [canDeleteUsers, setCanDeleteUsers] = useState(false);
  const [canEditCardPaystatus, setCanEditCardPaystatus] = useState(false);

  // get user informations and positions list
  useEffect(() => {
    checkAuthorization('user_supervisor', setCanManageUser);
    checkAuthorization('user_auth', setCanManageUserRoles);
    checkAuthorization('delete_auth', setCanDeleteCards);
    checkAuthorization('card_paystat', setCanEditCardPaystatus);
    checkAuthorization('can_delete_users', setCanDeleteUsers);
  }, []);

  const checkAuthorization = (authKey, setState) => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const authorization = userData[authKey];

    if (authorization) {
      setState(true);
    }
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  if (!userID) {
    return <Navigate to={{ pathname: '/404' }} />;
  }

  return (
    <div>
      <Helmet>
        <title>Anagrafica Utenti</title>
      </Helmet>
      <UserProvider userID={userID}>
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          {/* Tabs  */}
          <Box width="100%" height="100%">
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                variant={is600Screen ? 'fullWidth' : 'scrollable'}
                allowScrollButtonsMobile
                scrollButtons="auto"
                value={value}
                onChange={handleChangeUserTabs}
                aria-label="user tab panel"
              >
                <Tab
                  label="Anagrafica"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(0, 'quintuple')}
                  icon={<AssignmentIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Tesseramento"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(1, 'quintuple')}
                  icon={<BadgeIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Entità"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(2, 'quintuple')}
                  icon={<AccountTreeIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Cariche"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(3, 'quintuple')}
                  icon={<GppGoodIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Badge"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(4, 'quintuple')}
                  icon={<VerifiedIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Autorizzazioni"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(5, 'quintuple')}
                  icon={<AdminPanelSettingsIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Note"
                  sx={{ minWidth: 'fit-content', flex: 1 }}
                  {...a11yProps(6, 'quintuple')}
                  icon={<CommentIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* User data tab */}
            <MultipleTabPanel value={value} index={0}>
              <UserDataTab
                userID={userID}
                canManageUser={canManageUser}
                canManageUserRoles={canManageUserRoles}
                canDeleteUsers={canDeleteUsers}
              />
            </MultipleTabPanel>

            {/* Cards tab */}
            <MultipleTabPanel value={value} index={1}>
              <CardsTab
                userID={userID}
                canManageUser={canManageUser}
                canDeleteCards={canDeleteCards}
                canEditCardPaystatus={canEditCardPaystatus}
              />
            </MultipleTabPanel>

            {/* Entities */}
            <MultipleTabPanel value={value} index={2}>
              <EntitiesTab userID={userID} />
            </MultipleTabPanel>

            {/* Positions tab */}
            <MultipleTabPanel value={value} index={3}>
              <PositionsTab />
            </MultipleTabPanel>

            {/* Badge */}
            <MultipleTabPanel value={value} index={4}>
              <BadgesTab />
            </MultipleTabPanel>

            {/* Authorization tab */}
            <MultipleTabPanel value={value} index={5}>
              <AuthorizationsTab
                value={value}
                userID={userID}
                canManageUserRoles={canManageUserRoles}
              />
            </MultipleTabPanel>

            {/* Notes tab */}
            <MultipleTabPanel value={value} index={6}>
              <NotesTab userID={userID} />
            </MultipleTabPanel>
          </Box>

        </Container>

      </UserProvider>
    </div>
  );
}
